import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BAAPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Business Associate Agreement
          </h1>
          <p className="text-lg text-gray-600">
            Effective Date: Upon Acceptance
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="pb-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
            <div className="prose prose-lg max-w-none">
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-gray-700 leading-relaxed mb-6">
                  This Business Associate Agreement ("BAA") is entered into between the dental practice or other entity
                  accepting this BAA ("Covered Entity") and <strong className="text-gray-900">Asha Health, Inc., a Delaware
                  corporation</strong> ("Business Associate" or "Asha"). Covered Entity and Asha may each be referred to as a
                  "Party" and collectively as the "Parties."
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  This BAA forms part of the agreement between the Parties governing Asha's services, including the
                  applicable Terms of Service and any Statement of Work or order form (collectively, the "Services
                  Agreement").
                </p>

                {/* Acceptance Box */}
                <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-8">
                  <p className="font-bold text-gray-900 mb-4">
                    BY CHECKING THE BOX LABELED "I ACCEPT THE BUSINESS ASSOCIATE AGREEMENT," EXECUTING A DOCUMENT THAT
                    INCORPORATES THIS BAA, OR OTHERWISE ELECTRONICALLY ACCEPTING THIS BAA, THE PERSON ACCEPTING:
                  </p>
                  <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                    <li>Acknowledges that they have read, understood, and agree to be bound by this BAA;</li>
                    <li>Represents and warrants that they have authority to bind Covered Entity; and</li>
                    <li>Confirms that they are authorized to enter into a HIPAA business associate agreement on behalf of Covered Entity.</li>
                  </ol>
                </div>
              </div>

              {/* Article 1 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">ARTICLE 1: DEFINITIONS</h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-semibold mb-3">1.1 HIPAA Definitions</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      The following terms shall have the meanings assigned to them under the HIPAA Rules: "Breach," "Data
                      Aggregation," "Designated Record Set," "Disclosure," "Electronic Protected Health Information," "Health
                      Care Operations," "Individual," "Protected Health Information," "Required by Law," "Secretary," "Security
                      Incident," "Subcontractor," "Unsecured Protected Health Information," and "Use."
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      "HIPAA Rules" means the Privacy, Security, Breach Notification, and Enforcement Rules at 45 C.F.R. Parts
                      160 and 164, as amended.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      "PHI" means Protected Health Information received from Covered Entity, or created, received, maintained,
                      or transmitted by Asha on behalf of Covered Entity. PHI does not include information that has been
                      de-identified in accordance with 45 C.F.R. § 164.514 or information that is not subject to HIPAA.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Terms used but not otherwise defined in this BAA shall have the meanings assigned to them under HIPAA.
                    </p>
                  </div>
                </div>
              </div>

              {/* Article 2 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">ARTICLE 2: PERMITTED USES AND DISCLOSURES</h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-semibold mb-3">2.1 Performance of Services</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Asha may Use and Disclose PHI as necessary to provide, support, secure, administer, improve, and perform
                      the services and activities described in the Services Agreement, including practice-management,
                      patient-communication, scheduling, insurance, claims, payment, billing, revenue-cycle-management,
                      migration, integration, support, analytics, automation, and related services.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Asha may rely on Covered Entity's instructions, configurations, authorizations, representations, and
                      information in performing the Services.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">2.2 Management, Administration, and Legal Responsibilities</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Asha may Use PHI for its proper management and administration and to carry out its legal responsibilities.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Asha may Disclose PHI for those purposes if:
                    </p>
                    <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                      <li>The Disclosure is Required by Law; or</li>
                      <li>Asha obtains reasonable assurances from the recipient that the PHI will remain confidential, will be
                      Used or further Disclosed only as Required by Law or for the purpose for which it was Disclosed, and the
                      recipient will notify Asha of any instance of which it becomes aware in which the confidentiality of the
                      PHI has been breached.</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">2.3 Data Aggregation</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Asha may provide Data Aggregation services relating to the Health Care Operations of Covered Entity and,
                      to the extent permitted by HIPAA, other covered entities for which Asha acts as a business associate.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">2.4 De-identification</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Asha may de-identify PHI in accordance with 45 C.F.R. § 164.514. Once information has been properly
                      de-identified and is no longer PHI, Asha may Use and Disclose that de-identified information for any
                      lawful purpose, subject to the Services Agreement and Applicable Law.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">2.5 Required by Law</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Asha may Use or Disclose PHI as Required by Law.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">2.6 Limitations</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Except as expressly permitted by this BAA or the HIPAA Rules, Asha shall not Use or Disclose PHI in a
                      manner that would violate the HIPAA Privacy Rule if performed by Covered Entity.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Asha shall comply with the minimum-necessary requirements of HIPAA to the extent those requirements apply
                      to Asha's Uses, Disclosures, and requests for PHI.
                    </p>
                  </div>
                </div>
              </div>

              {/* Article 3 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">ARTICLE 3: OBLIGATIONS OF BUSINESS ASSOCIATE</h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-semibold mb-3">3.1 Safeguards</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Asha shall use appropriate safeguards to prevent the Use or Disclosure of PHI other than as permitted by
                      this BAA and shall comply with the applicable requirements of the HIPAA Security Rule with respect to
                      Electronic Protected Health Information.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">3.2 Reporting</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Asha shall report to Covered Entity:
                    </p>
                    <ol className="list-decimal pl-6 space-y-2 text-gray-700 mb-3">
                      <li>Any Use or Disclosure of PHI not permitted by this BAA of which Asha becomes aware;</li>
                      <li>Any Breach of Unsecured PHI as required by 45 C.F.R. § 164.410; and</li>
                      <li>Any Security Incident of which Asha becomes aware.</li>
                    </ol>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Notification of a Breach of Unsecured PHI shall be provided without unreasonable delay and in no event
                      later than sixty (60) calendar days after discovery of the Breach, except to the extent a delay is
                      permitted or required under 45 C.F.R. § 164.412.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Any Breach notification shall include the information required by 45 C.F.R. § 164.410 to the extent such
                      information is reasonably available to Asha at the time of notification. Asha may provide additional
                      required information as it becomes available.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      The Parties acknowledge and agree that this Section constitutes notice of the ongoing existence and
                      occurrence of unsuccessful Security Incidents. Unsuccessful Security Incidents include pings, port scans,
                      blocked attacks, unsuccessful login attempts, unsuccessful denial-of-service attacks, malware blocked by
                      security controls, and similar events that do not result in unauthorized access, acquisition, Use,
                      Disclosure, modification, or destruction of PHI or material interference with system operations. No
                      additional or individual report of such unsuccessful Security Incidents shall be required.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Nothing in this Section requires Asha to characterize an event as a Breach before Asha has completed a
                      reasonable investigation and, when applicable, the risk assessment contemplated by 45 C.F.R. § 164.402.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Unless otherwise expressly stated in the Services Agreement, Asha is not responsible for providing
                      notifications directly to Individuals, the media, state authorities, or the Secretary on Covered Entity's
                      behalf. Any assistance requested by Covered Entity beyond Asha's obligations under HIPAA may be subject to
                      mutually agreed terms and fees.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">3.3 Subcontractors</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      In accordance with 45 C.F.R. §§ 164.308(b)(2) and 164.502(e)(1)(ii), Asha shall ensure that any
                      Subcontractor that creates, receives, maintains, or transmits PHI on Asha's behalf agrees in writing to
                      restrictions, conditions, and requirements applicable to that PHI that are no less protective than those
                      required by HIPAA.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Asha may select, replace, and use Subcontractors in its discretion, subject to its compliance with this
                      Section.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">3.4 Access</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Upon Covered Entity's written request, Asha shall make PHI maintained by Asha in a Designated Record Set
                      available to Covered Entity as reasonably necessary for Covered Entity to satisfy its obligations under
                      45 C.F.R. § 164.524.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Unless otherwise agreed in writing, Asha may refer any request received directly from an Individual to
                      Covered Entity, and Covered Entity shall be responsible for evaluating and responding to the request.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">3.5 Amendment</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Upon Covered Entity's written direction, Asha shall make amendments to PHI maintained by Asha in a
                      Designated Record Set, or take other measures reasonably necessary for Covered Entity to satisfy its
                      obligations under 45 C.F.R. § 164.526.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Unless otherwise agreed in writing, Asha may refer any amendment request received directly from an
                      Individual to Covered Entity.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">3.6 Accounting of Disclosures</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Asha shall maintain and make available to Covered Entity the information required for Covered Entity to
                      provide an accounting of Disclosures under 45 C.F.R. § 164.528, to the extent applicable to Asha.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Unless otherwise agreed in writing, Asha may refer any accounting request received directly from an
                      Individual to Covered Entity.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">3.7 Delegated Privacy Rule Obligations</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      To the extent Asha expressly agrees to carry out an obligation of Covered Entity under the HIPAA Privacy
                      Rule, Asha shall comply with the requirements of the Privacy Rule applicable to Covered Entity in
                      performing that obligation.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Nothing in this Section makes Asha responsible for a Covered Entity obligation that Asha has not expressly
                      agreed to perform under the Services Agreement.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">3.8 Availability of Records</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Asha shall make its internal practices, books, and records relating to its Use and Disclosure of PHI
                      available to the Secretary for purposes of determining compliance with the HIPAA Rules.
                    </p>
                  </div>
                </div>
              </div>

              {/* Article 4 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">ARTICLE 4: OBLIGATIONS OF COVERED ENTITY</h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-semibold mb-3">4.1 Lawful Disclosure and Instructions</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Covered Entity represents and warrants that:
                    </p>
                    <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                      <li>Covered Entity may lawfully Disclose PHI to Asha;</li>
                      <li>Asha's Use and Disclosure of PHI to perform the Services in accordance with Covered Entity's
                      instructions is permitted by HIPAA and Applicable Law;</li>
                      <li>Covered Entity has obtained and will maintain all notices, consents, permissions, and authorizations
                      required for Asha to perform the Services; and</li>
                      <li>Covered Entity will not request or instruct Asha to Use or Disclose PHI in a manner that would violate
                      HIPAA if performed by Covered Entity, except where HIPAA expressly permits a business associate to engage
                      in the applicable activity.</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">4.2 Notice of Privacy Practices</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Covered Entity shall notify Asha of any limitation in Covered Entity's Notice of Privacy Practices, but
                      only to the extent the limitation may affect Asha's permitted Use or Disclosure of PHI.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">4.3 Changes in Permission</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Covered Entity shall notify Asha of any change in, or revocation of, an Individual's permission to Use or
                      Disclose PHI, but only to the extent the change or revocation may affect Asha's permitted Use or
                      Disclosure of PHI.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">4.4 Restrictions</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Covered Entity shall notify Asha of any restriction on the Use or Disclosure of PHI to which Covered
                      Entity has agreed or is required to abide under 45 C.F.R. § 164.522, but only to the extent the
                      restriction may affect Asha's permitted Use or Disclosure of PHI.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">4.5 Accuracy and Implementation</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Covered Entity is responsible for the accuracy, completeness, and lawfulness of the PHI, instructions,
                      configurations, and other information it provides to Asha.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Covered Entity shall be responsible for implementing any restriction, correction, amendment, revocation,
                      or other instruction within systems or processes controlled by Covered Entity and for notifying Asha
                      sufficiently in advance to allow Asha to implement any corresponding change reasonably required within the
                      Services.
                    </p>
                  </div>
                </div>
              </div>

              {/* Article 5 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">ARTICLE 5: TERM AND TERMINATION</h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-semibold mb-3">5.1 Term</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      This BAA becomes effective upon its acceptance and remains effective while Asha creates, receives,
                      maintains, or transmits PHI on behalf of Covered Entity.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      The termination or expiration of the Services Agreement shall not terminate any provision of this BAA that
                      must remain effective for as long as Asha retains PHI.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">5.2 Termination for Cause</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Covered Entity may terminate this BAA and the affected Services if Covered Entity determines that Asha has
                      violated a material term of this BAA and:
                    </p>
                    <ol className="list-decimal pl-6 space-y-2 text-gray-700 mb-3">
                      <li>Covered Entity provides Asha with reasonably detailed written notice describing the alleged violation;</li>
                      <li>If cure is possible, Asha fails to cure the violation within thirty (30) days after receiving the
                      notice or within another reasonable period agreed by the Parties; or</li>
                      <li>Cure is not possible.</li>
                    </ol>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      If termination is not feasible, Covered Entity may report the violation to the Secretary as required by
                      HIPAA.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Nothing in this Section expands Covered Entity's monetary remedies or modifies the limitations,
                      exclusions, exclusive remedies, notice requirements, claim periods, or dispute procedures contained in the
                      Services Agreement.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">5.3 Effect of Termination</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Upon termination of this BAA, Asha shall, if feasible, return to Covered Entity or destroy PHI that Asha
                      maintains and no longer requires for a permitted purpose.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Notwithstanding the foregoing, Asha may retain PHI:
                    </p>
                    <ol className="list-decimal pl-6 space-y-2 text-gray-700 mb-3">
                      <li>As Required by Law;</li>
                      <li>As reasonably necessary for Asha's proper management and administration or to carry out its legal
                      responsibilities;</li>
                      <li>To establish, exercise, or defend legal claims;</li>
                      <li>Where return or destruction is not feasible; or</li>
                      <li>In archives, disaster-recovery systems, backups, security logs, or similar systems where deletion of
                      particular information is not reasonably practicable, provided that the information is not restored or
                      otherwise accessed except for disaster recovery, security, legal, or compliance purposes.</li>
                    </ol>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      For as long as Asha retains PHI following termination, Asha shall:
                    </p>
                    <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                      <li>Continue to apply the protections of this BAA and the applicable requirements of the HIPAA Security
                      Rule;</li>
                      <li>Limit further Uses and Disclosures to the purposes for which the PHI was retained or that make its
                      return or destruction infeasible; and</li>
                      <li>Return or destroy the retained PHI when retention is no longer necessary and return or destruction
                      becomes feasible.</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">5.4 Survival</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Asha's obligations under Section 5.3 shall survive termination of this BAA for as long as Asha retains PHI.
                    </p>
                  </div>
                </div>
              </div>

              {/* Article 6 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">ARTICLE 6: ALLOCATION OF RISK AND RELATIONSHIP TO SERVICES AGREEMENT</h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-semibold mb-3">6.1 Incorporation of Services Agreement</h4>
                    <p className="text-gray-700 leading-relaxed">
                      This BAA forms part of the Services Agreement. The warranty disclaimers, exclusive remedies, limitations
                      and exclusions of liability, damages exclusions, aggregate liability caps, indemnification provisions,
                      claim-notice requirements, contractual limitation periods, arbitration provisions, class-action waivers,
                      and other risk-allocation provisions in the Services Agreement apply to this BAA and to all claims arising
                      out of or relating to PHI, privacy, security, Security Incidents, Breaches, or Asha's performance or
                      alleged nonperformance of this BAA.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">6.2 Order of Precedence</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      In the event of a conflict:
                    </p>
                    <ol className="list-decimal pl-6 space-y-2 text-gray-700 mb-3">
                      <li>The liability limitations, damages exclusions, indemnification provisions, exclusive remedies, and
                      dispute-resolution provisions of the Terms of Service shall control to the maximum extent permitted by
                      law;</li>
                      <li>This BAA shall control solely with respect to the Parties' operational obligations concerning PHI; and</li>
                      <li>The remainder of the Services Agreement shall control with respect to the Services and the Parties'
                      commercial relationship.</li>
                    </ol>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      Nothing in this BAA creates an indemnification obligation, uncapped liability, or obligation to pay
                      damages, penalties, notification expenses, forensic expenses, monitoring expenses, attorneys' fees, or
                      other costs beyond the obligations expressly stated in the Services Agreement.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Nothing in this Article limits the authority of a governmental regulator or excuses either Party from
                      performing an obligation that HIPAA does not permit the Parties to waive.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">6.3 No Third-Party Beneficiaries</h4>
                    <p className="text-gray-700 leading-relaxed">
                      This BAA is solely for the benefit of Asha and Covered Entity. It does not create any right, remedy, or
                      cause of action in favor of any Individual or other third party.
                    </p>
                  </div>
                </div>
              </div>

              {/* Article 7 */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">ARTICLE 7: MISCELLANEOUS</h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-semibold mb-3">7.1 Regulatory References</h4>
                    <p className="text-gray-700 leading-relaxed">
                      A reference in this BAA to HIPAA or a regulatory provision means that law or provision as currently in
                      effect or as subsequently amended.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">7.2 Amendment</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      The Parties shall take such action as is reasonably necessary to amend this BAA to comply with changes to
                      the HIPAA Rules or other Applicable Law.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Asha may update this BAA when reasonably necessary to reflect changes in Applicable Law, regulatory
                      guidance, the Services, or Asha's privacy or security practices, subject to any applicable amendment and
                      notice provisions in the Services Agreement. No amendment shall materially reduce a protection required by
                      HIPAA.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">7.3 Interpretation</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Any ambiguity in this BAA shall be interpreted to permit the Parties to comply with the HIPAA Rules while
                      preserving the allocation of commercial risk established by the Services Agreement to the maximum extent
                      permitted by law.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">7.4 No Agency</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Nothing in this BAA creates a partnership, joint venture, fiduciary relationship, employment relationship,
                      or agency relationship between the Parties, except to the limited extent agency treatment is independently
                      required under Applicable Law based on the Parties' actual conduct.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">7.5 Assignment, Governing Law, and Disputes</h4>
                    <p className="text-gray-700 leading-relaxed">
                      The assignment, governing-law, venue, arbitration, class-action waiver, and dispute-resolution provisions
                      of the Services Agreement apply to this BAA.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">7.6 Electronic Acceptance and Records</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Electronic acceptance of this BAA shall have the same legal effect as a handwritten signature. Asha may
                      maintain electronic records showing the identity of the accepting person, the Covered Entity represented,
                      the date and time of acceptance, and the version of the BAA accepted.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">7.7 Entire Agreement Concerning PHI</h4>
                    <p className="text-gray-700 leading-relaxed">
                      This BAA and the Services Agreement constitute the entire agreement between the Parties concerning Asha's
                      role as Covered Entity's business associate and supersede prior or contemporaneous business associate
                      agreements, representations, or understandings concerning the same subject matter, except where expressly
                      stated otherwise in a document signed by an authorized officer of Asha.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold mb-3">7.8 Severability and Waiver</h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      If any provision of this BAA is determined to be invalid or unenforceable, that provision shall be
                      enforced to the maximum extent permitted by law, and the remaining provisions shall remain in effect.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      A waiver must be in writing and applies only to the specific circumstance for which it is given. A failure
                      or delay in enforcing a provision is not a waiver.
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Section */}
              <div>
                <h3 className="text-2xl font-bold mb-4">Contact Information</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Questions and notices concerning this BAA may be directed to:
                </p>
                <div className="bg-gray-50 rounded-lg p-6">
                  <p className="font-semibold text-gray-900 mb-2">Asha Health, Inc.</p>
                  <p className="text-gray-700">Email: info@asha.health</p>
                  <p className="text-gray-700">Website: www.asha.health</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BAAPage;
